import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Copy, ShoppingBag, Clock, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";

// Render text with **bold** and clickable URLs
function RichNote({ text }) {
  const URL_RE = /(https?:\/\/[^\s<>"\')]+)/gi;
  const BOLD_RE = /\*\*([^*]+)\*\*/g;

  const renderInline = (str, keyBase) => {
    const parts = [];
    let last = 0;
    let m;
    const re = new RegExp(URL_RE);
    while ((m = re.exec(str)) !== null) {
      if (m.index > last) parts.push(str.slice(last, m.index));
      parts.push(
        <a
          key={`${keyBase}-u-${m.index}`}
          href={m[0]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-700 break-all"
        >
          {m[0]}
        </a>
      );
      last = m.index + m[0].length;
    }
    if (last < str.length) parts.push(str.slice(last));
    return parts;
  };

  const segments = [];
  let last = 0;
  let m;
  const re = new RegExp(BOLD_RE);
  let idx = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) segments.push(<span key={`p-${idx++}`}>{renderInline(text.slice(last, m.index), idx)}</span>);
    segments.push(<strong key={`b-${idx++}`} className="font-bold text-foreground">{renderInline(m[1], idx)}</strong>);
    last = m.index + m[0].length;
  }
  if (last < text.length) segments.push(<span key={`p-${idx++}`}>{renderInline(text.slice(last), idx)}</span>);

  return (
    <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">{segments}</p>
  );
}

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

function YouTubeEmbed({ url }) {
  const id = getYouTubeId(url);
  if (!id) return null;
  return (
    <div className="mt-2">
      <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">
        Tutorial
      </p>
      <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
        <iframe
          src={`https://www.youtube.com/embed/${id}`}
          title="Tutorial"
          className="absolute top-0 left-0 w-full h-full rounded-md border border-border"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
}

function statusLabel(sub) {
  const expired =
    sub.status === "expired" || (sub.days_left != null && sub.days_left < 0);
  if (sub.status === "pending") return { text: "PENDING", cls: "badge-unpaid", icon: Clock };
  if (expired) return { text: "EXPIRED", cls: "badge-overdue", icon: AlertTriangle };
  if (sub.days_left != null && sub.days_left <= 3)
    return { text: "EXPIRING", cls: "badge-due-today", icon: Clock };
  return { text: "ACTIVE", cls: "badge-paid", icon: CheckCircle2 };
}

function SubCard({ sub }) {
  const [showPw, setShowPw] = useState(false);
  const meta = statusLabel(sub);
  const isExpired =
    sub.status === "expired" || (sub.days_left != null && sub.days_left < 0);
  const showCreds =
    sub.status === "active" && sub.shared_account && !isExpired;
  const copy = (v) => copyToClipboard(v);
  return (
    <div className="glass-card rounded-lg p-5" data-testid={`sub-card-${sub.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
            {sub.plan?.tool_name || "—"}
          </p>
          <h3 className="font-heading font-bold text-lg mt-0.5">{sub.plan?.name || "Plan"}</h3>
        </div>
        <span
          className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-wider px-2 py-1 rounded-sm ${meta.cls}`}
        >
          <meta.icon className="w-3 h-3" />
          {meta.text}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Expires</p>
          <p className="font-mono text-sm mt-0.5">
            {sub.expire_date ? sub.expire_date.slice(0, 10) : "—"}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Days Left</p>
          <p className="font-mono text-sm mt-0.5">
            {sub.days_left != null ? (sub.days_left < 0 ? `${-sub.days_left}d overdue` : `${sub.days_left}d`) : "—"}
          </p>
        </div>
      </div>

      {showCreds ? (
        <div className="mt-5 border-t border-border pt-4 space-y-3">
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
            Login Credentials
          </p>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Email</p>
            <div className="flex items-center justify-between gap-2 mt-1 bg-secondary/40 rounded-md px-3 py-2">
              <code className="font-mono text-xs truncate">{sub.shared_account.login_email || "—"}</code>
              <button
                onClick={() => copy(sub.shared_account.login_email)}
                className="text-muted-foreground hover:text-foreground"
                data-testid={`copy-email-${sub.id}`}
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Password</p>
            <div className="flex items-center justify-between gap-2 mt-1 bg-secondary/40 rounded-md px-3 py-2">
              <code className="font-mono text-xs truncate">
                {showPw ? sub.shared_account.login_password || "—" : "•".repeat((sub.shared_account.login_password || "").length || 8)}
              </code>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowPw((v) => !v)}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid={`toggle-pw-${sub.id}`}
                >
                  {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
                <button
                  onClick={() => copy(sub.shared_account.login_password)}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid={`copy-pw-${sub.id}`}
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
          {sub.shared_account.notes && (
            <RichNote text={sub.shared_account.notes} />
          )}
          {sub.shared_account.tutorial_video_url && (
            <YouTubeEmbed url={sub.shared_account.tutorial_video_url} />
          )}
        </div>
      ) : isExpired ? (
        <div className="mt-5 border-t border-border pt-4">
          <div className="flex items-start gap-2 rounded-md bg-red-500/10 border border-red-500/20 p-3">
            <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-red-700 dark:text-red-300">
              <p className="font-semibold">Subscription expired</p>
              <p className="mt-0.5 text-red-600/80 dark:text-red-400/80">
                Renew to regain access to login credentials.
              </p>
            </div>
          </div>
        </div>
      ) : sub.status === "pending" ? (
        <div className="mt-5 border-t border-border pt-4">
          <div className="flex items-start gap-2 rounded-md bg-yellow-500/10 border border-yellow-500/20 p-3">
            <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-yellow-700 dark:text-yellow-300">
              <p className="font-semibold">Waiting for admin verification</p>
              <p className="mt-0.5 text-yellow-600/80 dark:text-yellow-400/80">
                Login credentials will appear here once your payment is approved (usually within 30 minutes).
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mt-5">
        <Link to={`/account/plans?buy=${sub.plan?.id || ""}`}>
          <Button size="sm" variant="outline" className="w-full" data-testid={`renew-${sub.id}`}>
            Renew
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function AccountDashboard() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get("/user/subscriptions");
      setSubs(data);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    intervalRef.current = setInterval(() => load(true), 15000); // poll every 15s
    return () => clearInterval(intervalRef.current);
  }, []);

  const manualRefresh = async () => {
    await load();
    toast.success("Refreshed");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">
            My Subscriptions
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Your active AI tool subscriptions and login credentials.
            {lastUpdated && (
              <span className="ml-2 text-xs font-mono opacity-70">
                · auto-refresh on
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={manualRefresh}
            disabled={refreshing}
            data-testid="refresh-subs"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Link to="/account/plans">
            <Button data-testid="cta-buy-plan">
              <ShoppingBag className="w-4 h-4 mr-2" /> Buy Plan
            </Button>
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : subs.length === 0 ? (
        <div className="glass-card rounded-lg p-10 text-center">
          <p className="font-heading font-bold text-lg">No subscriptions yet.</p>
          <p className="text-sm text-muted-foreground mt-1 mb-5">
            Browse our plans and grab AI tool access at a fraction of the cost.
          </p>
          <Link to="/account/plans">
            <Button data-testid="empty-buy">
              Browse Plans <ShoppingBag className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {subs.map((s) => (
            <SubCard key={s.id} sub={s} />
          ))}
        </div>
      )}
    </div>
  );
}
